/**
 * This module manages form validation, including individual input validations and reCAPTCHA checks.
 * It orchestrates the validation process, ensuring that all input fields within the form meet
 * their specified validation criteria, and that forms containing a reCAPTCHA are properly verified.
 */
(($) => {
    'use strict';

    /**
     * Validates all input elements within a form that are marked for validation.
     * It uses the jQuery `validate` method extended to individual inputs to check each one.
     * @param {jQuery} $form - The form element containing inputs to validate.
     * @returns {boolean} - True if all inputs are valid, false otherwise.
     */
    const validateInputs = ($form) => {
        const $inputsToValidate = $form.find('[data-validate]');
        let isFormValid = true;

        $inputsToValidate.each((index, input) => {
            isFormValid = $(input).validate() && isFormValid;
        });

        return isFormValid;
    };

    /**
     * Validates Google reCAPTCHA within the form, ensuring it's completed if present.
     * @param {jQuery} $form - The form element containing the reCAPTCHA to validate.
     * @returns {boolean} - True if reCAPTCHA is valid or not present, false if invalid.
     */
    const validateRecaptcha = ($form) => {
        const $grecaptcha = $form.find('.g-recaptcha');
        if ($grecaptcha.length) {
            const grecaptchaResponse = $grecaptcha
                .find('textarea[name=g-recaptcha-response]')
                .val();

            if (grecaptchaResponse.length === 0) {
                const message = $grecaptcha.data('required-message');
                $grecaptcha
                    .next('.validation-error')
                    .find('span')
                    .text(message);
                $grecaptcha.addClass('has-error');

                return false;
            } else {
                $grecaptcha.removeClass('has-error');
            }
        }

        return true;
    };

    /**
     * Public jQuery function to validate a form. This function integrates input validation
     * and reCAPTCHA validation. It triggers custom events if validations fail, and moves focus
     * to the first invalid input for accessibility.
     * @returns {boolean} - True if the entire form is valid, false otherwise.
     */
    $.fn.validateForm = function() {
        const $form = this;
        let isValid = true;

        if (!validateInputs($form)) {
            $form.trigger('inputsInvalid');
            isValid = false;

            // Focus on the first invalid input
            const $firstInvalidInput = $form.find('.has-error').first().find('input, select, textarea, button');
            if ($firstInvalidInput.length) {
                $firstInvalidInput.trigger('focus');
            }
        }

        if (!validateRecaptcha($form)) {
            $form.trigger('recaptchaInvalid');
            isValid = false;
        }

        return isValid;
    };

    /**
     * Public jQuery function to reset the validation state of a form. This function
     * clears all input fields and validation states, effectively resetting the form.
     */
    $.fn.resetForm = function() {
        const $form = this;
        $form.trigger('reset');
        $form.find('.has-error, .success').removeClass('has-error success');
    };
})(jQuery);
